import { RingProgress } from '@mantine/core';
import {
  IconTicket,
  IconCheck,
  IconClock,
  IconStar,
  IconArrowRight,
  IconAlertTriangle,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Badge, Box, Button, Card, Group, Progress, SimpleGrid, Stack, Text, Title } from '@/components/ui';
import {
  helpdeskStats,
  ticketsByStatus,
  ticketsByPriority,
  mockTickets,
} from '../mockData/helpdesk';

const StatCard = ({
  title,
  value,
  subtitle,
  icon: Icon,
  color
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ElementType;
  color: string;
}) => (
  <Card>
    <Group justify="space-between" mb="xs">
      <Text size="sm" c="dimmed">{title}</Text>
      <Box
        style={{
          width: 40,
          height: 40,
          borderRadius: 8,
          backgroundColor: `var(--mantine-color-${color}-light)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon size={20} color={`var(--mantine-color-${color}-filled)`} />
      </Box>
    </Group>
    <Text size="xl" fw={700}>{value}</Text>
    {subtitle && (
      <Text size="xs" c="dimmed" mt="xs">{subtitle}</Text>
    )}
  </Card>
);

const formatTime = (date: Date) => {
  return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(date);
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'new': return 'blue';
    case 'open': return 'cyan';
    case 'in-progress': return 'yellow';
    case 'pending': return 'orange';
    case 'resolved': return 'green';
    case 'closed': return 'gray';
    default: return 'gray';
  }
};

const getPriorityColor = (priority: string) => {
  switch (priority) {
    case 'urgent': return 'red';
    case 'high': return 'orange';
    case 'medium': return 'yellow';
    case 'low': return 'green';
    default: return 'gray';
  }
};

export default function HelpdeskDashboardPage() {
  const navigate = useNavigate();
  const recentTickets = mockTickets.slice(0, 5);
  const urgentTickets = mockTickets.filter(t => t.priority === 'urgent' || t.priority === 'high');
  const totalTickets = ticketsByStatus.reduce((acc, s) => acc + s.count, 0);

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <Box>
          <Title order={2}>Helpdesk Dashboard</Title>
          <Text c="dimmed" size="sm">Manage support tickets and knowledge base</Text>
        </Box>
        <Group>
          <Button variant="light" onClick={() => navigate('knowledge')}>Knowledge Base</Button>
          <Button onClick={() => navigate('tickets')}>View Tickets</Button>
        </Group>
      </Group>

      {/* Stats Grid */}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
        <StatCard
          title="Open Tickets"
          value={helpdeskStats.openTickets}
          subtitle={`+${helpdeskStats.ticketsChange}% this week`}
          icon={IconTicket}
          color="blue"
        />
        <StatCard
          title="Resolved Today"
          value={helpdeskStats.resolvedToday}
          icon={IconCheck}
          color="green"
        />
        <StatCard
          title="Avg Response Time"
          value={`${helpdeskStats.avgResponseTime}m`}
          icon={IconClock}
          color="orange"
        />
        <StatCard
          title="Satisfaction"
          value={`${helpdeskStats.customerSatisfaction}/5`}
          subtitle={`${helpdeskStats.slaCompliance}% SLA compliance`}
          icon={IconStar}
          color="yellow"
        />
      </SimpleGrid>

      {/* Ticket Distribution */}
      <SimpleGrid cols={{ base: 1, lg: 2 }}>
        {/* By Status */}
        <Card>
          <Text fw={600} mb="md">Tickets by Status</Text>
          <Group align="center" gap="xl">
            <RingProgress
              size={140}
              thickness={16}
              sections={ticketsByStatus.map(s => ({
                value: (s.count / totalTickets) * 100,
                color: s.color,
              }))}
              label={
                <Box ta="center">
                  <Text size="xl" fw={700}>{totalTickets}</Text>
                  <Text size="xs" c="dimmed">Total</Text>
                </Box>
              }
            />
            <Stack gap="xs" style={{ flex: 1 }}>
              {ticketsByStatus.map(status => (
                <Group key={status.status} justify="space-between">
                  <Group gap="sm">
                    <Box
                      w={12}
                      h={12}
                      style={{ borderRadius: 4, backgroundColor: `var(--mantine-color-${status.color}-filled)` }}
                    />
                    <Text size="sm">{status.status}</Text>
                  </Group>
                  <Text size="sm" fw={500}>{status.count}</Text>
                </Group>
              ))}
            </Stack>
          </Group>
        </Card>

        {/* By Priority */}
        <Card>
          <Text fw={600} mb="md">Tickets by Priority</Text>
          <Stack gap="md">
            {ticketsByPriority.map(priority => (
              <Group key={priority.priority} justify="space-between">
                <Group gap="sm" style={{ flex: 1 }}>
                  <Text size="sm" w={60}>{priority.priority}</Text>
                  <Progress
                    value={(priority.count / totalTickets) * 100}
                    size="lg"
                    radius="xl"
                    style={{ flex: 1 }}
                    color={priority.color}
                  />
                </Group>
                <Text size="sm" fw={500} w={30} ta="right">{priority.count}</Text>
              </Group>
            ))}
          </Stack>
        </Card>
      </SimpleGrid>

      {/* Recent Tickets and Urgent Alerts */}
      <SimpleGrid cols={{ base: 1, lg: 2 }}>
        {/* Recent Tickets */}
        <Card>
          <Group justify="space-between" mb="md">
            <Text fw={600}>Recent Tickets</Text>
            <Button
              variant="subtle"
              size="xs"
              rightSection={<IconArrowRight size={14} />}
              onClick={() => navigate('tickets')}
            >
              View All
            </Button>
          </Group>
          <Stack gap="sm">
            {recentTickets.map(ticket => (
              <Group key={ticket.id} justify="space-between" p="sm" style={{ backgroundColor: 'var(--mantine-color-gray-light)', borderRadius: 8 }}>
                <Box style={{ flex: 1 }}>
                  <Group gap="xs" mb={4}>
                    <Text size="sm" fw={500} lineClamp={1}>{ticket.subject}</Text>
                  </Group>
                  <Group gap="xs">
                    <Badge size="xs" color={getStatusColor(ticket.status)}>{ticket.status}</Badge>
                    <Badge size="xs" variant="light" color={getPriorityColor(ticket.priority)}>{ticket.priority}</Badge>
                    <Text size="xs" c="dimmed">{ticket.ticketNumber}</Text>
                  </Group>
                </Box>
                <Avatar size="sm" radius="xl" color="blue">
                  {ticket.customerName.split(' ').map(n => n[0]).join('')}
                </Avatar>
              </Group>
            ))}
          </Stack>
        </Card>

        {/* Urgent/High Priority */}
        <Card>
          <Group justify="space-between" mb="md">
            <Group gap="xs">
              <IconAlertTriangle size={18} color="var(--mantine-color-red-filled)" />
              <Text fw={600}>Urgent & High Priority</Text>
            </Group>
            <Badge size="sm" color="red">{urgentTickets.length} tickets</Badge>
          </Group>
          <Stack gap="sm">
            {urgentTickets.length === 0 ? (
              <Box ta="center" py="xl">
                <IconCheck size={48} color="var(--mantine-color-green-filled)" />
                <Text size="sm" c="dimmed" mt="md">No urgent tickets!</Text>
              </Box>
            ) : (
              urgentTickets.map(ticket => (
                <Box
                  key={ticket.id}
                  p="sm"
                  style={{
                    backgroundColor: 'var(--mantine-color-red-light)',
                    borderRadius: 8,
                    borderLeft: `4px solid var(--mantine-color-${getPriorityColor(ticket.priority)}-filled)`,
                  }}
                >
                  <Group justify="space-between" mb="xs">
                    <Text size="sm" fw={500}>{ticket.subject}</Text>
                    <Badge size="xs" color={getPriorityColor(ticket.priority)}>{ticket.priority}</Badge>
                  </Group>
                  <Group justify="space-between">
                    <Text size="xs" c="dimmed">{ticket.customerName}</Text>
                    {ticket.slaDeadline && (
                      <Text size="xs" c={ticket.slaDeadline < new Date() ? 'red' : 'orange'}>
                        SLA: {formatTime(ticket.slaDeadline)}
                      </Text>
                    )}
                  </Group>
                </Box>
              ))
            )}
          </Stack>
        </Card>
      </SimpleGrid>
    </Stack>
  );
}
